import { PATHS_WITH_SECTIONS } from '@/router/RoutesUtils'
import { RoutesData } from '@/router/RoutesData'

// New users, regardless of role, land on the marketplace after completing the
// account/profile creation flow. Existing users keep the default redirect
// (`useDefaultAuthRedirect`) used by sign-in and the auth guards.
export const NEW_USER_AUTH_REDIRECT = RoutesData.RMarketplace.path

// Bare /myReferrals routes through MyReferralsRedirect, which resolves the
// persisted last-visited tab (or the flag-aware default) so the loaded page
// and the selected menu tab always match (ENG-1567)
export const useDefaultAuthRedirect = () => PATHS_WITH_SECTIONS.MY_REFERRALS

// Location shape captured by the auth guards as `state.from` (react-router
// stores navigation state as `any`).
interface ReturnLocation {
  pathname?: string
  search?: string
  hash?: string
}

// Paths that must never be a post-login return target (would loop or make no
// sense to land on).
const BLOCKED_RETURN_PATHS = ['/signIn', '/signOut']

/**
 * Resolve the safe return path a user should land on after signing in
 * (ENG-1714). Deep links from emails (e.g. `/case/{id}`) send logged-out users
 * through the auth guard, which stashes the intended location as `state.from`;
 * without this, sign-in dropped them on the default dashboard.
 *
 * Returns a same-origin relative path string, or null when there is no valid
 * destination (so callers fall back to the default redirect).
 *
 * Only relative, same-origin paths are honored — an absolute or
 * protocol-relative value (`//evil.com`, `https://…`) is rejected to avoid
 * open-redirect abuse.
 */
export const getSafeReturnPath = (
  from: ReturnLocation | null | undefined
): string | null => {
  const pathname = from?.pathname
  if (
    !pathname ||
    !pathname.startsWith('/') ||
    // `//host` is treated as protocol-relative (absolute) by browsers.
    pathname.startsWith('//')
  ) {
    return null
  }
  if (
    BLOCKED_RETURN_PATHS.some(
      blocked => pathname === blocked || pathname.startsWith(`${blocked}/`)
    )
  ) {
    return null
  }
  return `${pathname}${from?.search ?? ''}${from?.hash ?? ''}`
}
